/*
  LIOR - product page in the style of iOS 27 (assets/lior-ios-product.css)

  The theme stays in charge of the data: its gallery (Swiper), its variant
  picker, its quantity field and its add to cart. This file only drives what
  you see and touch:
    - a glass page control under the photos that follows the gallery
    - a lens of glass that slides (on springs, X and Y apart, stretching a
      little with its speed) to the size or option you pick, and the picked
      value next to the option's name
    - the discount ("−25%") worked out from the two prices
    - the details below as an inset list whose rows open and close on a spring
      that can be reversed mid-flight
    - a check that pops into the buy button once the item is in the cart, a
      shake if the cart says no
    - a buy bar that rises from the bottom once the buy button has scrolled
      away (it presses the theme's own button, and carries the arrival date so
      no second note has to float above it), and a share capsule that opens
      the phone's share sheet
    - "קנייה עכשיו": Shopify's dynamic "Buy it now" only speaks the store's
      default language (English) from inside a closed shadow root, so a Hebrew
      button takes its place: into the cart, then straight to checkout
  It talks to the theme only through the theme's own elements and events
  (publish/subscribe 'cart-update' / 'cart-error'), and keeps working if the
  theme re-renders parts of the section.
*/
(function () {
  'use strict';

  if (window.__liorIosProduct) return;
  window.__liorIosProduct = true;

  var D = document;
  var H = D.documentElement;
  var W = window;
  var mqReduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  var RTL = (H.getAttribute('dir') || D.body.getAttribute('dir') || 'rtl').toLowerCase() === 'rtl';

  function reduce() { return mqReduce.matches; }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function now() { return W.performance ? W.performance.now() : Date.now(); }
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  /* ------------------------------------------------------------------------
     Springs (damping ratio + response, as Apple describes them), re-targetable
     at any moment from the current value and velocity
     ------------------------------------------------------------------------ */
  function Spring(value) {
    this.x = value || 0;
    this.v = 0;
    this.t = this.x;
    this.config(1, 0.4);
  }
  Spring.prototype.config = function (zeta, response) {
    this.zeta = zeta;
    this.w = (2 * Math.PI) / response;
    return this;
  };
  Spring.prototype.to = function (target, velocity) {
    this.t = target;
    if (typeof velocity === 'number' && isFinite(velocity)) this.v = velocity;
    return this;
  };
  Spring.prototype.snap = function (value) {
    this.x = this.t = value;
    this.v = 0;
    return this;
  };
  Spring.prototype.step = function (dt) {
    var n = Math.max(1, Math.ceil(dt / 0.004));
    var h = dt / n;
    for (var i = 0; i < n; i++) {
      var a = -this.w * this.w * (this.x - this.t) - 2 * this.zeta * this.w * this.v;
      this.v += a * h;
      this.x += this.v * h;
    }
  };
  Spring.prototype.settled = function (eps, veps) {
    return Math.abs(this.x - this.t) < eps && Math.abs(this.v) < veps;
  };

  /* One display-synced loop for everything that moves */
  var Loop = {
    jobs: [],
    raf: 0,
    last: 0,
    add: function (job) {
      if (Loop.jobs.indexOf(job) === -1) Loop.jobs.push(job);
      if (!Loop.raf) {
        Loop.last = now();
        Loop.raf = W.requestAnimationFrame(Loop.tick);
      }
    },
    remove: function (job) {
      var i = Loop.jobs.indexOf(job);
      if (i !== -1) Loop.jobs.splice(i, 1);
    },
    tick: function (t) {
      var dt = clamp((t - Loop.last) / 1000, 0.001, 0.05);
      Loop.last = t;
      var jobs = Loop.jobs.slice();
      for (var i = 0; i < jobs.length; i++) {
        if (jobs[i](dt) === false) Loop.remove(jobs[i]);
      }
      Loop.raf = Loop.jobs.length ? W.requestAnimationFrame(Loop.tick) : 0;
    }
  };

  var BOUNCY = 'linear(0, 0.0348, 0.1083, 0.2124, 0.3269, 0.4412, 0.5405, 0.6376, 0.7219, 0.7928, 0.8469, 0.8941, 0.9308, 0.9567, 0.9775, 0.9923, 1.0022, 1.0081, 1.0118, 1.0134, 1.0137, 1.0131, 1.0119, 1.0104, 1.0089, 1.0074, 1.0059, 1.0047, 1.0036, 1.0027, 1.0019, 1.0014, 1.0009, 1.0005, 1.0003, 1.0001, 1)';
  function bouncy() {
    return W.CSS && CSS.supports && CSS.supports('transition-timing-function', 'linear(0, 1)') ? BOUNCY : 'cubic-bezier(0.34, 1.36, 0.64, 1)';
  }

  var ICON = {
    check: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>',
    share: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 14.5V3.5M8 7.5l4-4 4 4M8.5 10.5H7a2 2 0 0 0-2 2V19a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6.5a2 2 0 0 0-2-2h-1.5"/></svg>'
  };

  /* "₪1,190.00" -> 1190 */
  function priceNum(t) {
    var s = String(t || '').replace(/[^\d.,]/g, '');
    if (/,\d{2}$/.test(s) && s.indexOf('.') !== -1 && s.lastIndexOf('.') < s.lastIndexOf(',')) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
    var n = parseFloat(s);
    return isFinite(n) ? n : NaN;
  }

  /* ------------------------------------------------------------------------
     Mount on the product section
     ------------------------------------------------------------------------ */
  function mount(root) {
    var st = { alive: true, offs: [] };
    function on(el, type, fn, opt) {
      el.addEventListener(type, fn, opt);
      st.offs.push(function () { el.removeEventListener(type, fn, opt); });
    }
    function q(sel) { return root.querySelector(sel); }
    function qa(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
    function mainBtn() { return q('.main-product-form [name="add"]'); }

    /* ======================= First paint: the details rise in once ======================= */
    var inner = q('.main--product-content-inner');
    if (inner && !reduce() && !(W.Shopify && W.Shopify.designMode)) {
      Array.prototype.forEach.call(inner.children, function (el, i) { el.style.setProperty('--lip-i', String(Math.min(i, 12))); });
      root.classList.add('lip-enter');
      var enterT = setTimeout(function () {
        root.classList.remove('lip-enter');
        Array.prototype.forEach.call(inner.children, function (el) { el.style.removeProperty('--lip-i'); });
      }, 1700);
      st.offs.push(function () { clearTimeout(enterT); root.classList.remove('lip-enter'); });
    }

    /* Delivery: from stock (a green light) or made to order (a clock) */
    (function () {
      var fd = D.getElementById('lior-floating-delivery');
      var kind = 'stock';
      if (fd) kind = fd.hasAttribute('data-order-delivery') ? 'order' : 'stock';
      else {
        var d = q('.lior-delivery');
        if (d && d.textContent.indexOf('במלאי') === -1) kind = 'order';
      }
      root.setAttribute('data-lip-delivery', kind);
    })();

    /* ======================= The floating delivery note ======================= */
    /* It stays out of sight while the same line is on screen under the price, and
       lends its arrival date to that line */
    var fdEl = D.getElementById('lior-floating-delivery');
    var fdIO = null;
    var fdLine = null;
    function watchDelivery() {
      if (!fdEl || !('IntersectionObserver' in W)) return;
      var line = q('.price-wrapper .lior-delivery');
      if (line === fdLine) return;
      if (!fdIO) {
        fdIO = new IntersectionObserver(function (entries) {
          var e = entries[entries.length - 1];
          H.classList.toggle('lip-fd-hide', e.isIntersecting || e.boundingClientRect.top > 0);
        });
        st.offs.push(function () { fdIO.disconnect(); H.classList.remove('lip-fd-hide'); });
      }
      if (fdLine) fdIO.unobserve(fdLine);
      fdLine = line;
      if (!line) {
        H.classList.remove('lip-fd-hide');
        return;
      }
      /* decided now, so the note does not flash in before the observer reports */
      H.classList.toggle('lip-fd-hide', line.getBoundingClientRect().bottom > 0);
      fdIO.observe(line);
    }
    function paintEta() {
      var line = q('.price-wrapper .lior-delivery');
      if (!fdEl || !line) return;
      var dd = fdEl.querySelector('[data-delivery-date]');
      var txt = dd ? dd.textContent.trim() : '';
      var next = line.nextElementSibling;
      var eta = next && next.classList.contains('lip-eta') ? next : null;
      if (txt) {
        if (!eta) {
          eta = D.createElement('p');
          eta.className = 'lip-eta';
          line.insertAdjacentElement('afterend', eta);
        }
        if (eta.textContent !== txt) eta.textContent = txt;
      } else if (eta) eta.remove();
    }
    if (fdEl && 'MutationObserver' in W) {
      var fdMO = new MutationObserver(function () { paintEta(); paintBar(); });
      fdMO.observe(fdEl, { childList: true, subtree: true, characterData: true });
      st.offs.push(function () { fdMO.disconnect(); });
    }

    /* ======================= Gallery: a glass page control ======================= */
    var sw = null;
    var pager = null;
    var swOff = null;
    function mediaCount() { return parseInt(root.getAttribute('data-lip-media'), 10) || 0; }
    function unbindSwiper() {
      if (swOff) swOff();
      swOff = null;
      if (pager) pager.remove();
      pager = null;
      sw = null;
    }
    function paintPager() {
      if (!pager || !sw || sw.destroyed) return;
      var i = sw.params && sw.params.loop ? sw.realIndex : sw.activeIndex;
      var dots = pager.querySelectorAll('.lip-dot');
      for (var k = 0; k < dots.length; k++) {
        if (k === i) dots[k].setAttribute('aria-current', 'true');
        else dots[k].removeAttribute('aria-current');
      }
    }
    function bindSwiper() {
      var el = q('[data-swiper-main]');
      var host = el && el.closest('swiper-content');
      var inst = (el && el.swiper) || (host && (host.swiper || host.slider)) || null;
      if (inst && typeof inst.on !== 'function') inst = null;
      if (inst && inst === sw && pager && pager.isConnected) return true;
      unbindSwiper();
      if (!inst || inst.destroyed) return false;
      sw = inst;
      var n = (inst.slides || []).length;
      if (n < 2 || mediaCount() < 2) return true;
      var wrap = el.closest('.main--product-image-wrapper') || el.parentNode;
      pager = D.createElement('div');
      pager.className = 'lip-pager';
      var html = '<div class="lip-pager-in" role="group" aria-label="תמונות המוצר">';
      for (var i = 0; i < n; i++) html += '<button type="button" class="lip-dot" aria-label="תמונה ' + (i + 1) + ' מתוך ' + n + '"></button>';
      pager.innerHTML = html + '</div>';
      wrap.appendChild(pager);
      pager.addEventListener('click', function (e) {
        var b = e.target.closest('.lip-dot');
        if (!b || !sw) return;
        var idx = Array.prototype.indexOf.call(pager.querySelectorAll('.lip-dot'), b);
        if (sw.params.loop && sw.slideToLoop) sw.slideToLoop(idx);
        else sw.slideTo(idx);
      });
      var fn = function () { paintPager(); };
      inst.on('slideChange', fn);
      inst.on('realIndexChange', fn);
      swOff = function () {
        try {
          inst.off('slideChange', fn);
          inst.off('realIndexChange', fn);
        } catch (e) { /* the theme destroyed it */ }
      };
      paintPager();
      return true;
    }
    var swTries = 0;
    var swTimer = 0;
    (function waitSwiper() {
      if (!st.alive) return;
      if (bindSwiper() || swTries++ > 60) return;
      swTimer = setTimeout(waitSwiper, 100);
    })();
    st.offs.push(function () { clearTimeout(swTimer); unbindSwiper(); });

    /* ======================= Options: the picked value and the lens ======================= */
    var lenses = [];
    var lensMemory = {};
    function chipBox(label, box) {
      var x = 0;
      var y = 0;
      var el = label;
      while (el && el !== box) {
        x += el.offsetLeft;
        y += el.offsetTop;
        el = el.offsetParent;
      }
      if (el !== box) {
        var br = box.getBoundingClientRect();
        var r = label.getBoundingClientRect();
        return { x: r.left - br.left, y: r.top - br.top, w: r.width, h: r.height };
      }
      return { x: x, y: y, w: label.offsetWidth, h: label.offsetHeight };
    }
    function paintLens(L) {
      var s = L.el.style;
      var vx = Math.abs(L.x.v);
      var vy = Math.abs(L.y.v);
      /* a little stretch along the way it travels, from its speed */
      var sx = (1 + Math.min(vx / 3200, 0.14) - Math.min(vy / 7000, 0.05)) * L.p.x;
      var sy = (1 + Math.min(vy / 3200, 0.12) - Math.min(vx / 7000, 0.06)) * L.p.x;
      s.setProperty('--lip-lx', L.x.x.toFixed(2) + 'px');
      s.setProperty('--lip-ly', L.y.x.toFixed(2) + 'px');
      s.setProperty('--lip-lw', Math.max(0, L.w.x).toFixed(2) + 'px');
      s.setProperty('--lip-lh', Math.max(0, L.h.x).toFixed(2) + 'px');
      s.setProperty('--lip-lsx', sx.toFixed(4));
      s.setProperty('--lip-lsy', sy.toFixed(4));
    }
    function lensJob(dt) {
      var busy = false;
      for (var i = 0; i < lenses.length; i++) {
        var L = lenses[i];
        if (!L.moving) continue;
        L.x.step(dt);
        L.y.step(dt);
        L.w.step(dt);
        L.h.step(dt);
        L.p.step(dt);
        var done = L.x.settled(0.3, 6) && L.y.settled(0.3, 6) && L.w.settled(0.3, 6) && L.h.settled(0.3, 6) && L.p.settled(0.002, 0.02);
        if (done) {
          L.x.snap(L.x.t);
          L.y.snap(L.y.t);
          L.w.snap(L.w.t);
          L.h.snap(L.h.t);
          L.p.snap(L.p.t);
          L.moving = false;
        } else busy = true;
        paintLens(L);
      }
      return busy;
    }
    function lensFor(box, name) {
      for (var i = 0; i < lenses.length; i++) {
        if (lenses[i].box === box) {
          if (lenses[i].el.isConnected) return lenses[i];
          lenses.splice(i, 1);
          break;
        }
      }
      var el = D.createElement('span');
      el.className = 'lip-lens';
      el.setAttribute('aria-hidden', 'true');
      box.appendChild(el);
      box.classList.add('lip-has-lens');
      var L = {
        box: box,
        el: el,
        name: name,
        x: new Spring(0).config(0.8, 0.42),
        y: new Spring(0).config(0.84, 0.42),
        w: new Spring(0).config(1, 0.36),
        h: new Spring(0).config(1, 0.36),
        p: new Spring(1).config(1, 0.24),
        placed: false,
        moving: false
      };
      /* the theme may have re-rendered the picker: start from where the lens was */
      var m = lensMemory[name];
      if (m) {
        L.x.snap(m.x);
        L.y.snap(m.y);
        L.w.snap(m.w);
        L.h.snap(m.h);
        L.placed = true;
        paintLens(L);
      }
      lenses.push(L);
      return L;
    }
    function moveLens(L, input, animate) {
      var label = input && input.nextElementSibling;
      if (!label || !label.offsetWidth) {
        L.el.classList.remove('is-on');
        return;
      }
      var b = chipBox(label, L.box);
      if (L.placed && L.x.t === b.x && L.y.t === b.y && L.w.t === b.w && L.h.t === b.h) return;
      if (!L.placed || !animate || reduce()) {
        L.x.snap(b.x);
        L.y.snap(b.y);
        L.w.snap(b.w);
        L.h.snap(b.h);
        L.placed = true;
        L.moving = false;
        paintLens(L);
      } else {
        L.x.to(b.x);
        L.y.to(b.y);
        L.w.to(b.w);
        L.h.to(b.h);
        L.moving = true;
        Loop.add(lensJob);
      }
      lensMemory[L.name] = b;
      L.el.classList.add('is-on');
    }
    function scanOptions(animate) {
      qa('.product--options-set').forEach(function (os) {
        var list = os.querySelector('.product--variants-list');
        if (!list) return;
        var checked = list.querySelector('input:checked');
        var title = os.querySelector('.product--variants-title');
        var isColor = list.classList.contains('color-option');
        /* the store names its options in English ("Shoe size"); the page speaks Hebrew */
        if (title) {
          var nameEl = title.querySelector(':scope > span') || title;
          for (var nn = nameEl.firstChild; nn; nn = nn.nextSibling) {
            if (nn.nodeType !== 3 || !nn.nodeValue.trim()) continue;
            var raw = nn.nodeValue.trim().replace(/\s*:\s*$/, '');
            var he = optName(raw);
            if (he && he !== raw) nn.nodeValue = nn.nodeValue.replace(raw, he);
            break;
          }
        }
        /* the picked value next to the option's name (the theme shows it only for colours) */
        if (title && !isColor) {
          var ov = title.querySelector('.lip-optval');
          var v = checked ? checked.value : '';
          if (v && !ov) {
            ov = D.createElement('span');
            ov.className = 'lip-optval';
            /* inside the name, so a size guide beside it keeps its place */
            var first = title.querySelector(':scope > span');
            (first || title).appendChild(ov);
          }
          if (ov && ov.textContent !== v) ov.textContent = v;
        }
        if (isColor) return;
        /* short values (sizes) line up in an even grid */
        var short = true;
        var ins = list.querySelectorAll('input');
        for (var k = 0; k < ins.length; k++) {
          if (String(ins[k].value).length > 4) {
            short = false;
            break;
          }
        }
        if (short !== list.classList.contains('lip-grid')) list.classList.toggle('lip-grid', short);
        var box = list.closest('.product--variants-box') || list.parentNode;
        moveLens(lensFor(box, os.getAttribute('data-optionset') || String(qa('.product--options-set').indexOf(os))), checked, animate);
      });
    }
    /* Pressing the chosen chip presses the lens with it */
    var pressedLens = null;
    function lensRelease() {
      if (!pressedLens) return;
      pressedLens.p.to(1);
      pressedLens.moving = true;
      Loop.add(lensJob);
      pressedLens = null;
    }
    on(root, 'pointerdown', function (e) {
      var li = e.target.closest && e.target.closest('.variant--item');
      if (!li || reduce()) return;
      var input = li.querySelector('input:checked');
      if (!input) return;
      var box = li.closest('.lip-has-lens');
      for (var i = 0; i < lenses.length; i++) {
        if (lenses[i].box === box) {
          pressedLens = lenses[i];
          pressedLens.p.to(0.96);
          pressedLens.moving = true;
          Loop.add(lensJob);
          break;
        }
      }
    }, { passive: true });
    on(W, 'pointerup', lensRelease, true);
    on(W, 'pointercancel', lensRelease, true);

    /* ======================= Price: the discount ======================= */
    function paintOff() {
      qa('.price-wrapper .product--pricing').forEach(function (p) {
        var a = p.querySelector('.product--actual-price');
        var c = p.querySelector('.product--cut-price');
        var off = p.querySelector('.lip-off');
        var pct = 0;
        if (a && c) {
          var x = priceNum(a.textContent);
          var y = priceNum(c.textContent);
          if (y > x && x > 0) pct = Math.round((1 - x / y) * 100);
        }
        if (pct >= 1) {
          var txt = '−' + pct + '%';
          if (!off) {
            off = D.createElement('span');
            off.className = 'lip-off';
            off.setAttribute('dir', 'ltr');
            p.appendChild(off);
          }
          if (off.textContent !== txt) off.textContent = txt;
        } else if (off) off.remove();
      });
    }

    /* ======================= Quantity: the number answers the tap ======================= */
    function bump(el) {
      if (!el || reduce() || !el.animate) return;
      el.animate([{ transform: 'scale(1.24)' }, { transform: 'scale(1)' }], { duration: 460, easing: bouncy() });
    }
    on(root, 'click', function (e) {
      var b = e.target.closest && e.target.closest('.quantity-button');
      if (!b) return;
      var wrap = b.closest('.quantity--inner');
      var input = wrap && wrap.querySelector('.quantity-input');
      if (!input) return;
      var before = input.value;
      setTimeout(function () { if (input.value !== before) bump(input); }, 0);
    });

    /* ======================= Details: rows that open on a spring ======================= */
    var accs = typeof WeakMap === 'function' ? new WeakMap() : null;
    function accJob(a) {
      return function (dt) {
        a.s.step(dt);
        var done = a.s.settled(0.5, 4);
        if (done) a.s.snap(a.s.t);
        var h = Math.max(0, a.s.x);
        a.content.style.height = h.toFixed(1) + 'px';
        a.content.style.opacity = String(clamp((h / Math.max(a.full, 1)) * 1.6 - 0.3, 0, 1).toFixed(3));
        if (!done) return true;
        a.det.classList.remove('is-animating', 'is-closing');
        a.content.style.removeProperty('height');
        a.content.style.removeProperty('opacity');
        if (!a.want) {
          a.det.open = false;
          a.det.classList.remove('is-open');
        }
        a.running = false;
        return false;
      };
    }
    function toggleAcc(det, content) {
      var a = accs && accs.get(det);
      if (!a) {
        a = { det: det, content: content, s: new Spring(0), want: det.open, running: false, full: 0 };
        a.job = accJob(a);
        if (accs) accs.set(det, a);
      }
      a.want = !a.want;
      if (reduce()) {
        det.open = a.want;
        det.classList.toggle('is-open', a.want);
        return;
      }
      if (!a.running) a.s.snap(det.open ? content.offsetHeight : 0);
      if (a.want) {
        det.open = true;
        det.classList.add('is-open', 'is-animating');
        det.classList.remove('is-closing');
        a.s.config(1, 0.44);
      } else {
        det.classList.add('is-closing', 'is-animating');
        det.classList.remove('is-open');
        a.s.config(1, 0.36);
      }
      a.full = content.scrollHeight;
      a.s.to(a.want ? a.full : 0);
      content.style.height = Math.max(0, a.s.x).toFixed(1) + 'px';
      a.running = true;
      Loop.add(a.job);
    }
    on(root, 'click', function (e) {
      var sum = e.target.closest && e.target.closest('summary.product-accordion--heading');
      if (!sum) return;
      var det = sum.parentElement;
      if (!det || det.tagName !== 'DETAILS') return;
      var content = det.querySelector(':scope > .product-accordion--content');
      if (!content) return;
      /* ours alone: the theme's own toggle must not run as well */
      e.preventDefault();
      e.stopPropagation();
      toggleAcc(det, content);
    }, true);
    /* nor anything of the theme's that animates these rows on 'toggle' */
    on(root, 'toggle', function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('product-accordion--item')) e.stopPropagation();
    }, true);

    /* ======================= Share: the phone's own share sheet ======================= */
    function canonical() {
      var l = D.querySelector('link[rel="canonical"]');
      return l && l.href ? l.href : W.location.href.split('#')[0];
    }
    function buildShare() {
      var row = q('.product--social-share');
      if (!row || row.querySelector('.lip-share') || typeof navigator.share !== 'function') return;
      var b = D.createElement('button');
      b.type = 'button';
      b.className = 'lip-capsule lip-share';
      b.innerHTML = ICON.share + '<span>שיתוף</span>';
      row.insertBefore(b, row.firstChild);
      row.classList.add('lip-has-share');
      b.addEventListener('click', function () {
        var t = q('.product-title-heading');
        var p = navigator.share({ title: (t ? t.textContent : D.title).trim(), url: canonical() });
        if (p && p.catch) p.catch(function () { /* dismissed */ });
      });
    }

    /* ======================= Add to cart: a check, or a shake ======================= */
    var bar = null;
    var barBtn = null;
    var adding = null;
    var addTimer = 0;
    var doneTimer = 0;
    function decorate(btn, label) {
      if (!btn || btn.querySelector('.lip-atc-done')) return;
      var s = D.createElement('span');
      s.className = 'lip-atc-done';
      s.setAttribute('aria-hidden', 'true');
      s.innerHTML = ICON.check + '<span>' + label + '</span>';
      btn.appendChild(s);
    }
    function shake(el) {
      if (!el || reduce() || !el.animate) return;
      el.animate([
        { translate: '0 0' }, { translate: '-10px 0' }, { translate: '8px 0' }, { translate: '-5px 0' }, { translate: '3px 0' }, { translate: '0 0' }
      ], { duration: 460, easing: 'ease-out' });
    }
    function startAdd() {
      adding = { t: now() };
      clearTimeout(addTimer);
      addTimer = setTimeout(function () { finishAdd(null); }, 9000);
      if (barBtn) barBtn.classList.add('is-busy');
    }
    function finishAdd(ok) {
      if (!adding) return;
      adding = null;
      clearTimeout(addTimer);
      if (barBtn) barBtn.classList.remove('is-busy');
      var btns = [mainBtn(), barBtn];
      if (ok === true) {
        decorate(mainBtn(), 'נוסף לסל');
        btns.forEach(function (b) { if (b) b.classList.add('lip-added'); });
        clearTimeout(doneTimer);
        doneTimer = setTimeout(function () {
          [mainBtn(), barBtn].forEach(function (b) { if (b) b.classList.remove('lip-added'); });
        }, 1900);
        try { if (navigator.vibrate) navigator.vibrate(8); } catch (e) { /* not allowed */ }
      } else if (ok === false) {
        btns.forEach(shake);
      }
    }
    function cartErrorShown() {
      var err = q('[data-add-cart-error]');
      return !!(err && !err.classList.contains('hidden') && err.textContent.trim());
    }
    on(root, 'click', function (e) {
      var b = e.target.closest && e.target.closest('[name="add"]');
      if (b && !b.disabled && b.getAttribute('aria-disabled') !== 'true' && b === mainBtn()) startAdd();
    }, true);
    if (typeof W.subscribe === 'function') {
      try {
        var un1 = W.subscribe('cart-update', function () { finishAdd(true); });
        var un2 = W.subscribe('cart-error', function () { finishAdd(false); });
        st.offs.push(function () { if (typeof un1 === 'function') un1(); if (typeof un2 === 'function') un2(); });
      } catch (e) { /* no pub/sub */ }
    }
    /* If the theme publishes nothing, the request itself tells us */
    if (W.PerformanceObserver) {
      try {
        var po = new PerformanceObserver(function (list) {
          if (!adding) return;
          var es = list.getEntries();
          for (var i = 0; i < es.length; i++) {
            if (/\/cart\/add/.test(es[i].name) && es[i].startTime >= adding.t - 50) {
              setTimeout(function () { if (adding) finishAdd(!cartErrorShown()); }, 160);
              break;
            }
          }
        });
        po.observe({ type: 'resource' });
        st.offs.push(function () { po.disconnect(); });
      } catch (e) { /* not supported */ }
    }

    /* ======================= Buy now, in Hebrew ======================= */
    /* Shopify draws its "Buy it now" inside a closed shadow root, in the store's
       default language, with no key to translate it. This capsule does the same
       job in Hebrew: the item goes into the cart with the form's quantity and
       line properties (the delivery note too), and the page goes to checkout. */
    var buyBtn = null;
    var buying = false;
    function buyOn() { return root.getAttribute('data-lip-buynow') === 'true'; }
    function rootUrl() {
      var r = W.Shopify && W.Shopify.routes && W.Shopify.routes.root;
      return r ? (r.charAt(r.length - 1) === '/' ? r : r + '/') : '/';
    }
    function paintBuy() {
      if (!buyBtn) return;
      var m = mainBtn();
      var dis = !m || m.disabled || m.getAttribute('aria-disabled') === 'true';
      if (buyBtn.disabled !== dis) buyBtn.disabled = dis;
    }
    function ensureBuy() {
      if (!buyOn()) return;
      var m = mainBtn();
      var box = m && m.closest('.product-checkout-buttons');
      var pay = box && box.querySelector('[data-shopify="payment-button"], .shopify-payment-button');
      if (!pay) return;
      if (!buyBtn) {
        buyBtn = D.createElement('button');
        buyBtn.type = 'button';
        buyBtn.className = 'lip-buynow';
        buyBtn.innerHTML = '<span class="lip-buynow-t">קנייה עכשיו</span><span class="lip-spin" aria-hidden="true"><i></i></span>';
        buyBtn.addEventListener('click', buyNow);
        st.offs.push(function () { if (buyBtn) buyBtn.remove(); buyBtn = null; });
      }
      /* the theme may have re-drawn the buttons: follow them */
      if (buyBtn.previousElementSibling !== pay) pay.insertAdjacentElement('afterend', buyBtn);
      paintBuy();
      watchBtn();
    }
    function buyDone(ok, msg) {
      buying = false;
      if (!buyBtn) return;
      buyBtn.classList.remove('is-busy');
      buyBtn.removeAttribute('aria-busy');
      if (ok) return;
      var err = q('[data-add-cart-error]');
      if (err) {
        err.textContent = msg || 'לא הצלחנו להוסיף את המוצר לסל. נסו שוב.';
        err.classList.remove('hidden');
      }
      shake(buyBtn);
    }
    /* The variant on screen. The theme keeps its hidden id input disabled on
       products with options and supplies the id itself, so read the picked
       values against the variants the theme printed, then fall back to the
       picked value's own variant, the URL, and the form. */
    function pickedVariantId() {
      var sets = qa('variants-set');
      for (var s = 0; s < sets.length; s++) {
        var vs = sets[s];
        if (vs.closest('sticky-buy-button, .sticky-buy-button-wrapper, .sticky--product-buttons')) continue;
        var data = vs.querySelector('script[data-name="main-product"]');
        var list = null;
        try { list = data ? JSON.parse(data.textContent) : null; } catch (x) { list = null; }
        if (!list || !list.length) continue;
        var groups = vs.querySelectorAll('optionset');
        var sel = [];
        for (var g = 0; g < groups.length; g++) {
          var c = groups[g].querySelector('input:checked');
          var sl = groups[g].querySelector('select');
          sel.push(c ? c.value : sl ? sl.value : null);
        }
        /* the dropdown picker has no option sets, just one select per option */
        if (!sel.length) {
          Array.prototype.forEach.call(vs.querySelectorAll('select:not([name="id"])'), function (x) { sel.push(x.value); });
        }
        if (sel.length) {
          for (var i = 0; i < list.length; i++) {
            var o = list[i].options || [];
            var hit = o.length === sel.length;
            for (var k = 0; hit && k < sel.length; k++) if (String(o[k]) !== String(sel[k])) hit = false;
            if (hit) return String(list[i].id);
          }
        }
        var chk = vs.querySelector('input.productOption:checked[data-variant-id]');
        if (chk && chk.getAttribute('data-variant-id')) return chk.getAttribute('data-variant-id');
      }
      var u = '';
      try { u = new URLSearchParams(W.location.search).get('variant') || ''; } catch (x) { u = ''; }
      return /^\d+$/.test(u) ? u : '';
    }
    function buyNow(e) {
      if (e) e.preventDefault();
      var m = mainBtn();
      var form = m && (m.form || m.closest('form'));
      if (buying || !form || !buyBtn || buyBtn.disabled) return;
      if (form.reportValidity && !form.reportValidity()) return;
      buying = true;
      buyBtn.classList.add('is-busy');
      buyBtn.setAttribute('aria-busy', 'true');
      var err = q('[data-add-cart-error]');
      if (err) err.classList.add('hidden');
      var fd = new FormData(form);
      fd.delete('sections');
      fd.delete('sections_url');
      var vid = pickedVariantId();
      if (vid) fd.set('id', vid);
      if (!fd.get('id')) {
        buyDone(false, 'בחרו את האפשרויות של המוצר ונסו שוב.');
        return;
      }
      fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function (r) {
        return r.json().then(function (j) { return { ok: r.ok, j: j || {} }; }, function () { return { ok: r.ok, j: {} }; });
      }).then(function (res) {
        if (!res.ok || res.j.status) throw new Error(res.j.description || res.j.message || '');
        W.location.href = rootUrl() + 'checkout';
      }).catch(function (x) {
        buyDone(false, x && x.message);
      });
    }
    /* back from checkout, the page may come out of the cache mid-spin */
    on(W, 'pageshow', function (e) { if (e.persisted) buyDone(true); });

    /* ======================= The buy bar ======================= */
    var barBits = null;
    var barPast = false;
    var barEnd = false;
    var barOn = false;
    var barIO = null;
    var barTarget = null;
    /* the store names its options in English ("Shoe size"); the bar speaks Hebrew */
    var OPT_HE = { 'shoe size': 'מידה', 'size': 'מידה', 'belt size': 'מידה', 'color': 'צבע', 'colour': 'צבע' };
    function optName(n) {
      n = String(n || '').trim();
      if (/[֐-׿]/.test(n)) return n;
      return OPT_HE[n.toLowerCase()] || '';
    }
    function selectedText() {
      var parts = [];
      qa('.product--options-set').forEach(function (os) {
        var c = os.querySelector('.product--variants-list input:checked');
        if (c) parts.push({ n: optName(os.getAttribute('data-optionset')), v: c.value });
      });
      if (parts.length === 1) return (parts[0].n ? parts[0].n + ' ' : '') + parts[0].v;
      return parts.map(function (p) { return p.v; }).join(' · ');
    }
    function paintBar() {
      if (!bar) return;
      var t = q('.product-title-heading');
      var title = t ? t.textContent.trim() : '';
      if (barBits.t.textContent !== title) barBits.t.textContent = title;
      var p = q('.price-wrapper .product--pricing');
      var a = p && p.querySelector('.product--actual-price');
      var c = p && p.querySelector('.product--cut-price');
      var sel = selectedText();
      var html = (a ? '<b>' + esc(a.textContent.trim()) + '</b>' : '') + (c ? '<s>' + esc(c.textContent.trim()) + '</s>' : '') + (sel ? ' · ' + esc(sel) : '');
      if (barBits.lastP !== html) {
        barBits.p.innerHTML = html;
        barBits.lastP = html;
      }
      var img = q('.main--product-item img') || D.querySelector('sticky-buy-button img');
      var src = img ? (img.currentSrc || img.src) : '';
      if (src && barBits.img.getAttribute('src') !== src) barBits.img.setAttribute('src', src);
      barBits.imgWrap.hidden = !src;
      var m = mainBtn();
      var lab = m ? (m.querySelector('[data-atc-text]') || m).textContent.trim() : '';
      lab = lab || 'הוספה לסל';
      if (barBits.label.textContent !== lab) barBits.label.textContent = lab;
      var dis = !m || m.disabled;
      if (barBtn.disabled !== dis) barBtn.disabled = dis;
      var eta = etaText();
      if (barBits.eta.textContent !== eta) barBits.eta.textContent = eta;
      barBits.eta.hidden = !eta;
      bar.classList.toggle('has-eta', !!eta);
    }
    /* the arrival date ("הגעה משוערת: 18 בנובמבר"); items in stock carry no date,
       so their short promise from under the price stands in */
    function etaText() {
      var dd = fdEl && fdEl.querySelector('[data-delivery-date]');
      var t = dd ? dd.textContent.trim() : '';
      if (t) return t;
      var line = q('.price-wrapper .lior-delivery');
      return line ? line.textContent.replace(/\s+/g, ' ').trim() : '';
    }
    function showBar() {
      var show = barPast && !barEnd;
      if (show === barOn || !bar) return;
      barOn = show;
      bar.classList.toggle('is-in', show);
      H.classList.toggle('lip-bar-on', show);
      if (show) paintBar();
      liftOthers(show);
    }
    /* Other floating buttons that apps and the theme pin to the bottom corners
       (accessibility, chat, WhatsApp, back to top) would sit on the bar: while
       it is up they rise above it on the same spring, then settle back. */
    var lifted = [];
    function liftOthers(show) {
      var keep = [];
      lifted.forEach(function (x) {
        if (show && x.el.isConnected) { keep.push(x); return; }
        try {
          x.anim.onfinish = function () { x.anim.cancel(); };
          x.anim.reverse();
        } catch (e) { /* gone */ }
      });
      lifted = keep;
      if (!show || !bar || !W.Element.prototype.animate) return;
      var br = bar.getBoundingClientRect();
      var top = br.top - 10;
      var vw = W.innerWidth;
      var seen = lifted.map(function (x) { return x.el; });
      var cands = D.querySelectorAll('body > *, body > * > *');
      for (var i = 0; i < cands.length; i++) {
        var el = cands[i];
        if (seen.indexOf(el) !== -1 || el === bar || bar.contains(el) || el.contains(bar)) continue;
        if (/^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/.test(el.tagName)) continue;
        var cs = W.getComputedStyle(el);
        if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) continue;
        var r = el.getBoundingClientRect();
        /* small floating things only: never move a banner, a drawer or a sheet */
        if (!r.width || !r.height || r.width > vw * 0.6 || r.height > W.innerHeight * 0.6) continue;
        if (r.bottom <= top || r.top >= br.bottom) continue;
        var dy = Math.ceil(r.bottom - top);
        var anim = el.animate([{ translate: '0 0' }, { translate: '0 ' + (-dy) + 'px' }], {
          duration: reduce() ? 1 : 520, easing: bouncy(), fill: 'forwards'
        });
        lifted.push({ el: el, anim: anim });
      }
    }
    st.offs.push(function () { liftOthers(false); });
    /* the theme may switch the button off without touching its text */
    var btnMO = null;
    var btnSeen = null;
    function watchBtn() {
      var m = mainBtn();
      if ((!bar && !buyBtn) || m === btnSeen || !('MutationObserver' in W)) return;
      if (!btnMO) {
        btnMO = new MutationObserver(function () { paintBar(); paintBuy(); });
        st.offs.push(function () { btnMO.disconnect(); });
      }
      btnMO.disconnect();
      btnSeen = m;
      if (m) btnMO.observe(m, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    }
    function watchTarget() {
      watchBtn();
      var m = mainBtn();
      var target = m ? m.closest('.product-checkout-buttons') || m : null;
      if (!barIO || target === barTarget) return;
      if (barTarget) barIO.unobserve(barTarget);
      barTarget = target;
      if (target) barIO.observe(target);
    }
    function buildBar() {
      if (root.getAttribute('data-lip-bar') !== 'true' || !mainBtn() || !('IntersectionObserver' in W)) return;
      bar = D.createElement('div');
      bar.className = 'lip-bar';
      bar.setAttribute('dir', RTL ? 'rtl' : 'ltr');
      bar.innerHTML = '<div class="lip-bar-in" role="region" aria-label="קנייה מהירה">' +
        '<span class="lip-bar-img" aria-hidden="true"><img alt="" decoding="async"></span>' +
        '<span class="lip-bar-txt"><span class="lip-bar-t"></span><span class="lip-bar-p"></span><span class="lip-bar-eta" hidden></span></span>' +
        '<button type="button" class="lip-bar-btn"><span class="lip-bar-btn-t"></span>' +
        '<span class="lip-spin" aria-hidden="true"><i></i></span>' +
        '<span class="lip-atc-done" aria-hidden="true">' + ICON.check + '<span>נוסף</span></span></button></div>';
      D.body.appendChild(bar);
      barBtn = bar.querySelector('.lip-bar-btn');
      barBits = {
        t: bar.querySelector('.lip-bar-t'),
        p: bar.querySelector('.lip-bar-p'),
        img: bar.querySelector('.lip-bar-img img'),
        imgWrap: bar.querySelector('.lip-bar-img'),
        label: bar.querySelector('.lip-bar-btn-t'),
        eta: bar.querySelector('.lip-bar-eta'),
        lastP: null
      };
      bar.setAttribute('data-delivery', root.getAttribute('data-lip-delivery') || 'stock');
      H.classList.add('lip-bar-ready');
      st.offs.push(function () {
        if (bar) bar.remove();
        bar = null;
        barBtn = null;
        H.classList.remove('lip-bar-ready', 'lip-bar-on');
      });
      barBtn.addEventListener('click', function () {
        var m = mainBtn();
        if (!m || m.disabled || m.getAttribute('aria-disabled') === 'true') return;
        m.click();
      });
      barIO = new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        barPast = !e.isIntersecting && e.boundingClientRect.top < 0;
        showBar();
      });
      st.offs.push(function () { barIO.disconnect(); });
      watchTarget();

      /* Steps aside at the very end of a long page, so the footer stays reachable */
      var endT = 0;
      function atEnd() {
        endT = 0;
        var docH = Math.max(H.scrollHeight, D.body.scrollHeight);
        barEnd = docH > W.innerHeight + 400 && W.innerHeight + W.scrollY >= docH - 40;
        showBar();
      }
      on(W, 'scroll', function () {
        if (!endT) endT = W.requestAnimationFrame(atEnd);
      }, { passive: true });
      paintBar();
    }

    /* ======================= Keep in step with the theme ======================= */
    var scheduled = false;
    function refresh() {
      scheduled = false;
      if (!st.alive) return;
      bindSwiper();
      scanOptions(true);
      paintOff();
      paintEta();
      watchDelivery();
      buildShare();
      ensureBuy();
      watchTarget();
      paintBar();
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      Promise.resolve().then(refresh);
    }
    var mo = new MutationObserver(function (recs) {
      for (var i = 0; i < recs.length; i++) {
        var t = recs[i].target;
        /* the lens moving is not news */
        if (t.nodeType === 1 && (t.classList.contains('lip-lens') || t.classList.contains('lip-pager-in'))) continue;
        return schedule();
      }
    });
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    st.offs.push(function () { mo.disconnect(); });

    on(root, 'change', function (e) {
      var t = e.target;
      if (t && t.classList && t.classList.contains('productOption')) {
        scanOptions(true);
        paintBar();
      } else if (t && t.classList && t.classList.contains('quantity-input')) bump(t);
    });

    var rzT = 0;
    on(W, 'resize', function () {
      clearTimeout(rzT);
      rzT = setTimeout(function () { scanOptions(false); }, 120);
    });
    if (D.fonts && D.fonts.ready) D.fonts.ready.then(function () { if (st.alive) scanOptions(false); });
    on(W, 'load', function () { scanOptions(false); paintBar(); });

    /* The glass setting lives on the section; the bar sits in <body> */
    var glass = W.getComputedStyle(root).getPropertyValue('--lip-glass').trim();
    if (glass) H.style.setProperty('--lip-glass', glass);

    H.classList.add('lip-ready');
    scanOptions(false);
    paintOff();
    paintEta();
    watchDelivery();
    buildShare();
    ensureBuy();
    buildBar();

    return {
      root: root,
      destroy: function () {
        st.alive = false;
        Loop.remove(lensJob);
        clearTimeout(addTimer);
        clearTimeout(doneTimer);
        st.offs.forEach(function (f) { f(); });
        lenses.forEach(function (L) { L.el.remove(); });
        H.style.removeProperty('--lip-glass');
      }
    };
  }

  /* ------------------------------------------------------------------------
     Boot, and follow the theme editor
     ------------------------------------------------------------------------ */
  var current = null;
  function boot() {
    var root = D.querySelector('.lip[data-lip]');
    if (!root) return;
    if (current && current.root === root && root.isConnected) return;
    if (current) current.destroy();
    current = mount(root);
  }

  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  D.addEventListener('shopify:section:load', function () { boot(); });
  D.addEventListener('shopify:section:unload', function (e) {
    if (current && e.target && e.target.contains(current.root)) {
      current.destroy();
      current = null;
      H.classList.remove('lip-ready');
    }
  });
})();
